import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../../utils/cn';

/* -----------------------------------------------------------------------------
 * FieldSet
 * -------------------------------------------------------------------------- */

const FieldSet = React.forwardRef<
  HTMLFieldSetElement,
  React.FieldsetHTMLAttributes<HTMLFieldSetElement>
>(({ className, ...props }, ref) => (
  <fieldset ref={ref} className={cn('space-y-6 border-none p-0', className)} {...props} />
));
FieldSet.displayName = 'FieldSet';

/* -----------------------------------------------------------------------------
 * FieldLegend
 * -------------------------------------------------------------------------- */

const fieldLegendVariants = cva('', {
  variants: {
    variant: {
      legend: 'text-lg font-semibold text-foreground',
      label: 'text-sm font-semibold text-foreground',
    },
  },
  defaultVariants: {
    variant: 'legend',
  },
});

interface FieldLegendProps
  extends React.HTMLAttributes<HTMLLegendElement>, VariantProps<typeof fieldLegendVariants> {}

const FieldLegend = React.forwardRef<HTMLLegendElement, FieldLegendProps>(
  ({ className, variant, ...props }, ref) => (
    <legend ref={ref} className={cn(fieldLegendVariants({ variant }), className)} {...props} />
  ),
);
FieldLegend.displayName = 'FieldLegend';

/* -----------------------------------------------------------------------------
 * FieldGroup
 * -------------------------------------------------------------------------- */

const FieldGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-6', className)} {...props} />
  ),
);
FieldGroup.displayName = 'FieldGroup';

/* -----------------------------------------------------------------------------
 * Field
 * -------------------------------------------------------------------------- */

const fieldVariants = cva('flex gap-1', {
  variants: {
    orientation: {
      vertical: 'flex-col',
      horizontal: 'flex-row items-start justify-between',
      responsive:
        'flex-col @md/field-group:flex-row @md/field-group:items-start @md/field-group:justify-between',
    },
  },
  defaultVariants: {
    orientation: 'vertical',
  },
});

interface FieldProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof fieldVariants> {
  'data-invalid'?: boolean;
}

const Field = React.forwardRef<HTMLDivElement, FieldProps>(
  ({ className, orientation, ...props }, ref) => (
    <div
      ref={ref}
      role="group"
      className={cn(
        fieldVariants({ orientation }),
        'group/field',
        'data-[invalid=true]:text-red-500',
        className,
      )}
      {...props}
    />
  ),
);
Field.displayName = 'Field';

/* -----------------------------------------------------------------------------
 * FieldContent
 * -------------------------------------------------------------------------- */

const FieldContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-1 flex-col gap-1', className)} {...props} />
  ),
);
FieldContent.displayName = 'FieldContent';

/* -----------------------------------------------------------------------------
 * FieldLabel
 * -------------------------------------------------------------------------- */

interface FieldLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  asChild?: boolean;
}

const FieldLabel = React.forwardRef<HTMLLabelElement, FieldLabelProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'label';
    return (
      <Comp
        ref={ref}
        className={cn(
          'text-sm font-semibold text-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
          'group-data-[invalid=true]/field:text-red-500',
          className,
        )}
        {...props}
      />
    );
  },
);
FieldLabel.displayName = 'FieldLabel';

/* -----------------------------------------------------------------------------
 * FieldTitle
 * -------------------------------------------------------------------------- */

const FieldTitle = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'text-sm font-semibold text-foreground',
        'group-data-[invalid=true]/field:text-red-500',
        className,
      )}
      {...props}
    />
  ),
);
FieldTitle.displayName = 'FieldTitle';

/* -----------------------------------------------------------------------------
 * FieldDescription
 * -------------------------------------------------------------------------- */

const FieldDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('pb-1.5 text-sm text-muted-foreground text-pretty', className)}
    {...props}
  />
));
FieldDescription.displayName = 'FieldDescription';

/* -----------------------------------------------------------------------------
 * FieldSeparator
 * -------------------------------------------------------------------------- */

const FieldSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center gap-4 text-xs text-foreground-subtle', className)}
      {...props}
    >
      <div className="h-px flex-1 bg-border" />
      {children && <span>{children}</span>}
      <div className="h-px flex-1 bg-border" />
    </div>
  ),
);
FieldSeparator.displayName = 'FieldSeparator';

/* -----------------------------------------------------------------------------
 * FieldError
 * -------------------------------------------------------------------------- */

interface FieldErrorProps extends React.HTMLAttributes<HTMLDivElement> {
  errors?: Array<{ message?: string } | undefined>;
}

const FieldError = React.forwardRef<HTMLDivElement, FieldErrorProps>(
  ({ className, errors, children, ...props }, ref) => {
    const messages = errors?.filter((e) => e?.message).map((e) => e!.message);

    if (!messages?.length && !children) return null;

    return (
      <div ref={ref} role="alert" className={cn('text-sm text-red-500', className)} {...props}>
        {children ??
          (messages!.length === 1 ? (
            messages![0]
          ) : (
            <ul className="list-disc pl-4">
              {messages!.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          ))}
      </div>
    );
  },
);
FieldError.displayName = 'FieldError';

export {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
};
